// Global Instructions Rule Applied!
//
// Relationship Facts section — the shared knowledge ledger about an account
// (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md Phase 0). Standard kit: antd
// Table size='small' inside a FormSection, Add/Edit in an AntD Modal with the
// default footer, row actions per the word-button grammar: the ask (Accept)
// is primary, Reject/Archive are danger bordered, Edit/Confirm default
// bordered. Personal-type facts carry a lock (in-app only — never in a draft,
// brief or report). Agent-proposed rows show the agent's name and land
// 'proposed' until a human accepts them.
import React, { useMemo, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Select, Switch, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock, faPlus, faRobot } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { RelationshipFactRecord } from '../../../state_manager/data_models/relationships_model.ts'
import { RelationshipFactTypeModel } from '../../../state_manager/data_models/relationship_fact_type_model.ts'
import { useRelationshipFacts } from '../hooks/useRelationshipFacts.ts'
import {
  saveRelationshipFact,
  reviewRelationshipFact,
  archiveRelationshipFact,
  confirmRelationshipFact
} from '../services/relationshipsService.ts'

interface RelationshipFactsSectionProps {
  accountId: string | null | undefined
  /** On the prospect form: scope new facts to this process and show the
   *  account-level rows alongside. */
  prospectId?: string | null
  onRefreshReady?: (refreshFn: () => Promise<void>) => void
  className?: string
  title?: string
}

const STATUS_COLOR: Record<string, string> = {
  proposed: 'blue',
  active: 'green',
  superseded: 'default',
  archived: 'default',
  rejected: 'red'
}

const Importance = ({ n }: { n: number }) => (
  <span className='font-mono text-xs text-gray-500' title={`Importance ${n} of 5`}>
    {'●'.repeat(Math.max(0, Math.min(5, n)))}
    {'○'.repeat(5 - Math.max(0, Math.min(5, n)))}
  </span>
)

export function RelationshipFactsSection({
  accountId,
  prospectId = null,
  onRefreshReady,
  className,
  title = 'Relationship Facts'
}: RelationshipFactsSectionProps) {
  const [showHistory, setShowHistory] = useState(false)
  const { facts, loading, refetch } = useRelationshipFacts(accountId, { prospectId, includeHistory: showHistory })
  const factTypes = useSelector(
    (state: any) => (state.globalConfigs?.relationship_fact_types || []) as RelationshipFactTypeModel[]
  )
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<RelationshipFactRecord | null>(null)
  const [typeId, setTypeId] = useState<string | undefined>(undefined)
  const [content, setContent] = useState('')
  const [importance, setImportance] = useState(3)
  const [submitting, setSubmitting] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  React.useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  const token = () => getAccessToken(store.getState().auth)
  const selectedType = useMemo(() => factTypes.find((t) => t.id === typeId), [factTypes, typeId])

  const openAdd = () => {
    setEditing(null)
    setTypeId(factTypes[0]?.id)
    setContent('')
    setImportance(3)
    setModalOpen(true)
  }
  const openEdit = (row: RelationshipFactRecord) => {
    setEditing(row)
    setTypeId(row.relationship_facts__fact_type_id)
    setContent(row.relationship_facts__content)
    setImportance(row.relationship_facts__importance)
    setModalOpen(true)
  }

  const submit = async () => {
    if (!accountId || !typeId || !content.trim()) return
    setSubmitting(true)
    try {
      await saveRelationshipFact(
        editing
          ? { id: editing.relationship_facts__id, fact_type_id: typeId, content: content.trim(), importance }
          : { account_id: accountId, fact_type_id: typeId, content: content.trim(), importance, prospect_id: prospectId || null },
        token()
      )
      toast.success(editing ? 'Fact updated' : 'Fact added')
      setModalOpen(false)
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to save fact')
    } finally {
      setSubmitting(false)
    }
  }

  const run = async (row: RelationshipFactRecord, fn: () => Promise<void>, done: string) => {
    setBusyId(row.relationship_facts__id)
    try {
      await fn()
      toast.success(done)
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Action failed')
    } finally {
      setBusyId(null)
    }
  }

  const columns: ColumnsType<RelationshipFactRecord> = [
    {
      title: 'Type',
      dataIndex: 'relationship_fact_types__label',
      key: 'type',
      width: 170,
      render: (v: string, r) => (
        <span className='inline-flex items-center gap-1'>
          {r.relationship_fact_types__is_personal ? (
            <Tooltip title='Personal — held in-app only; never used in drafts or reports'>
              <FontAwesomeIcon icon={faLock} className='text-purple-600' />
            </Tooltip>
          ) : null}
          {v}
        </span>
      )
    },
    {
      title: 'Fact',
      dataIndex: 'relationship_facts__content',
      key: 'content',
      render: (v: string, r) => (
        <div className={r.relationship_facts__status === 'active' || r.relationship_facts__status === 'proposed' ? '' : 'text-gray-400'}>
          <div>{v}</div>
          {r.contacts__name || r.funds__name ? (
            <div className='text-xs text-gray-500'>
              {r.contacts__name ? <span>{r.contacts__name}</span> : null}
              {r.contacts__name && r.funds__name ? ' · ' : ''}
              {r.funds__name ? <span>{r.funds__name}</span> : null}
            </div>
          ) : null}
          {r.relationship_facts__status_reason && r.relationship_facts__status !== 'active' ? (
            <div className='text-xs text-gray-400'>{r.relationship_facts__status_reason}</div>
          ) : null}
        </div>
      )
    },
    { title: 'Importance', dataIndex: 'relationship_facts__importance', key: 'importance', width: 100, render: (n: number) => <Importance n={n} /> },
    {
      title: 'Source',
      key: 'source',
      width: 150,
      render: (_: unknown, r) =>
        r.relationship_facts__source === 'agent' ? (
          <Tooltip title={`Proposed by ${r.internal_agent_types__name ?? 'an agent'}`}>
            <Tag color='blue' icon={<FontAwesomeIcon icon={faRobot} />}>{r.internal_agent_types__name ?? 'Agent'}</Tag>
          </Tooltip>
        ) : (
          <span className='text-xs text-gray-500'>{r.prog__created_by_name ?? 'User'}</span>
        )
    },
    {
      title: 'Confirmed',
      dataIndex: 'relationship_facts__last_confirmed_at',
      key: 'confirmed',
      width: 110,
      render: (v: string) => dayjs(v).format('MM/DD/YYYY')
    },
    {
      title: 'Status',
      dataIndex: 'relationship_facts__status',
      key: 'status',
      width: 100,
      render: (v: string) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>
    },
    {
      title: '',
      key: 'actions',
      width: 210,
      align: 'right',
      render: (_: unknown, r) => {
        const busy = busyId === r.relationship_facts__id
        if (r.relationship_facts__status === 'proposed') {
          return (
            <span className='inline-flex items-center gap-1'>
              <Button type='primary' size='small' loading={busy}
                onClick={() => run(r, () => reviewRelationshipFact(r.relationship_facts__id, 'accept', null, token()), 'Fact accepted')}>
                Accept
              </Button>
              <Button danger size='small' loading={busy}
                onClick={() => run(r, () => reviewRelationshipFact(r.relationship_facts__id, 'reject', null, token()), 'Fact rejected')}>
                Reject
              </Button>
            </span>
          )
        }
        if (r.relationship_facts__status === 'active') {
          return (
            <span className='inline-flex items-center gap-1'>
              <Button size='small' onClick={() => openEdit(r)}>Edit</Button>
              <Button size='small' loading={busy}
                onClick={() => run(r, () => confirmRelationshipFact(r.relationship_facts__id, token()), 'Fact confirmed')}>
                Confirm
              </Button>
              <Popconfirm title='Archive this fact?' okText='Archive' onConfirm={() => run(r, () => archiveRelationshipFact(r.relationship_facts__id, null, token()), 'Fact archived')}>
                <Button danger size='small' loading={busy}>Archive</Button>
              </Popconfirm>
            </span>
          )
        }
        return null
      }
    }
  ]

  const typeOptions = factTypes.map((t) => ({ value: t.id, label: t.is_personal ? `${t.label} (personal)` : t.label }))

  return (
    <div data-component='RelationshipFactsSection' className={className}>
      <FormSection
        title={title}
        titleColor='blue'
        loading={loading && facts.length === 0}
        headerActions={
          <span className='inline-flex items-center gap-3'>
            <span className='inline-flex items-center gap-1 text-xs text-gray-600'>
              <Switch size='small' checked={showHistory} onChange={setShowHistory} /> Show history
            </span>
            <Button size='small' icon={<FontAwesomeIcon icon={faPlus} />} onClick={openAdd} disabled={!accountId || factTypes.length === 0}>
              Add
            </Button>
          </span>
        }
      >
        <Table<RelationshipFactRecord>
          rowKey='relationship_facts__id'
          size='small'
          pagination={false}
          columns={columns}
          dataSource={facts}
          loading={loading}
          locale={{ emptyText: <div className='text-center text-gray-500 text-sm py-2'>No relationship facts yet</div> }}
        />
      </FormSection>

      <Modal
        title={editing ? 'Edit fact' : 'Add fact'}
        open={modalOpen}
        okText={editing ? 'Save' : 'Add'}
        okButtonProps={{ disabled: !typeId || !content.trim() }}
        confirmLoading={submitting}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <div className='flex flex-col gap-3 pt-2'>
          <div>
            <div className='text-xs text-gray-600 mb-1'>Type</div>
            <Select className='w-full' options={typeOptions} value={typeId} onChange={(v) => setTypeId(v)} showSearch optionFilterProp='label' />
            {selectedType?.guidance ? <div className='text-xs text-gray-500 mt-1'>{selectedType.guidance}</div> : null}
            {selectedType?.is_personal ? (
              <div className='text-xs text-purple-700 mt-1'>
                <FontAwesomeIcon icon={faLock} /> Personal facts stay in-app; they are never used in drafts or reports.
              </div>
            ) : null}
          </div>
          <div>
            <div className='text-xs text-gray-600 mb-1'>Fact</div>
            <Input.TextArea rows={3} value={content} onChange={(e) => setContent(e.target.value)} maxLength={2000} showCount />
          </div>
          <div>
            <div className='text-xs text-gray-600 mb-1'>Importance</div>
            <Select className='w-40' value={importance} onChange={(v) => setImportance(v)}
              options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${n} of 5` }))} />
          </div>
          {editing && editing.relationship_facts__content !== content.trim() ? (
            <div className='text-xs text-gray-500'>Changing the text keeps the previous wording in the history.</div>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}

export default RelationshipFactsSection
